/** Sentry truncates tag values at 200 characters. */
const MAX_TAG_VALUE_LENGTH = 200;

export interface WebGLSupport {
  /** A WebGL context of any kind could be created. */
  supported: boolean;
  /**
   * A context exists, but only through a software rasterizer such as
   * SwiftShader — what the browser falls back to when the GPU is blocklisted
   * or hardware acceleration is off.
   */
  softwareOnly: boolean;
  /** The GPU or software renderer the browser reports, for grouping. */
  renderer?: string;
}

type GLContext = WebGLRenderingContext | WebGL2RenderingContext;

/**
 * Contexts are a scarce, per-page resource and the browser evicts the oldest
 * once it runs out, so a probe must hand its context back rather than wait for
 * garbage collection.
 */
const release = (gl: GLContext | null) => {
  gl?.getExtension('WEBGL_lose_context')?.loseContext();
};

const readRenderer = (gl: GLContext): string => {
  // Firefox reports the unmasked renderer through RENDERER and deprecates the
  // debug extension, so fall back to it rather than reporting nothing.
  const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
  const renderer = gl.getParameter(
    debugInfo ? debugInfo.UNMASKED_RENDERER_WEBGL : gl.RENDERER,
  );
  return String(renderer).slice(0, MAX_TAG_VALUE_LENGTH);
};

/** Creates and immediately releases throwaway contexts to see what WebGL the browser offers. */
export const probeWebGL = (): WebGLSupport => {
  try {
    const canvas = document.createElement('canvas');
    const gl = (canvas.getContext('webgl2') ??
      canvas.getContext('webgl')) as GLContext | null;
    if (!gl) {
      return { supported: false, softwareOnly: false };
    }

    const renderer = readRenderer(gl);
    release(gl);

    const hardware = document
      .createElement('canvas')
      .getContext('webgl', { failIfMajorPerformanceCaveat: true });
    release(hardware);

    return { supported: true, softwareOnly: !hardware, renderer };
  } catch {
    return { supported: false, softwareOnly: false };
  }
};

let cached: WebGLSupport | null = null;

/** The probe result, computed once per page load because every probe spends a context. */
export const getWebGLSupport = (): WebGLSupport => {
  cached ??= probeWebGL();
  return cached;
};
